import React, { useState } from "react";
import { ArrowLeft, TrendingUp, Info, Users, BarChart3, Coins } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { COIN_DEMO } from "@/data/mock";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function Sparkline({ data }) {
  const w = 600, h = 200;
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((d, i) => [(i / (data.length - 1)) * w, h - ((d - min) / (max - min)) * (h - 20) - 10]);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-48 w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="cg" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ff2e93" /><stop offset="50%" stopColor="#22e3a0" /><stop offset="100%" stopColor="#12c8ff" />
        </linearGradient>
        <linearGradient id="cgf" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22e3a0" stopOpacity="0.25" /><stop offset="100%" stopColor="#22e3a0" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#cgf)" />
      <path d={line} fill="none" stroke="url(#cg)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const RANGES = ["1H", "1D", "1W", "1M", "1Y", "ALL"];

export default function CoinDemo() {
  const navigate = useNavigate();
  const [range, setRange] = useState("1W");
  const c = COIN_DEMO;
  const notEnabled = () => toast("Trading is not enabled in the AuraMax prototype.", { description: "This page only evaluates future UX. No transaction occurs." });

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:px-6">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="grid h-9 w-9 place-items-center rounded-full text-white hover:bg-white/5"><ArrowLeft className="h-5 w-5" /></button>
        <span className="rounded-full border border-aura-gold/40 bg-aura-gold/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-aura-gold">Demo · Prototype data</span>
      </div>

      <div className="rounded-3xl border border-am-border bg-am-surface/60 p-5 md:p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-14 w-14 place-items-center rounded-2xl aura-gradient">
              <Coins className="h-7 w-7 text-black" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-xl font-extrabold text-white">{c.name}</h1>
                <span className="rounded-md bg-am-surface3 px-2 py-0.5 text-xs font-bold text-am-text2">{c.ticker}</span>
              </div>
              <p className="text-sm text-am-text2">{c.creator}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-extrabold text-white">${c.price}</div>
            <div className="flex items-center justify-end gap-1 text-sm font-semibold text-aura-mint"><TrendingUp className="h-4 w-4" />{c.change}</div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-am-border bg-am-bg/60 p-3">
          <Sparkline data={c.chart} />
          <div className="mt-2 flex justify-center gap-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setRange(r)} className={cn("rounded-lg px-3 py-1 text-xs font-semibold", range === r ? "bg-am-surface3 text-white" : "text-am-text2 hover:text-white")}>{r}</button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3">
          {[{ icon: BarChart3, label: "Market Cap", val: c.marketCap }, { icon: Users, label: "Holders", val: c.holders }, { icon: TrendingUp, label: "Volume 24H", val: c.volume }].map((s) => (
            <div key={s.label} className="rounded-2xl border border-am-border bg-am-bg/60 p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs text-am-text2"><s.icon className="h-3.5 w-3.5" />{s.label}</div>
              <div className="font-display text-lg font-bold text-white">{s.val}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl border border-am-border bg-am-bg/60 p-4">
          <h3 className="mb-1.5 text-sm font-bold text-white">About</h3>
          <p className="text-sm leading-relaxed text-am-text2">{c.about}</p>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-xl border border-aura-gold/25 bg-aura-gold/5 px-4 py-3 text-sm text-am-text2">
          <Info className="h-4 w-4 shrink-0 text-aura-gold" />
          Buying and selling are disabled. This screen exists only to evaluate future UX.
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button onClick={notEnabled} data-testid="coin-buy" className="rounded-full bg-white py-3 text-sm font-bold text-black transition-transform hover:scale-[1.01] active:scale-95">Buy</button>
          <button onClick={notEnabled} data-testid="coin-sell" className="rounded-full border border-am-border-strong py-3 text-sm font-bold text-white transition-colors hover:bg-white/5">Sell</button>
        </div>
      </div>
    </div>
  );
}
